// PainelFiltros.js — Os quatro grupos de filtro do catálogo (/livros):
// Categorias, Tipo, Preço e Disponibilidade. Daqui saem as DUAS aparências do
// mesmo filtro: a coluna fixa do desktop e o bottom-sheet do celular.
//
// Por que este arquivo existe: no celular a coluna de filtros não tinha nenhum
// modificador ativo (era tudo `md:`), então virava um bloco full-width com os
// quatro grupos abertos EMPILHADO ACIMA da lista. A primeira coisa que a pessoa
// via ao abrir o catálogo no telefone era um formulário, não os livros. Para dar
// um painel próprio ao celular sem duplicar marcação, o JSX saiu do GridProduto
// e virou este componente, com duas variantes da mesma árvore.
//
// REGRA DURA: este componente é APRESENTACIONAL. Todo o estado dos filtros
// continua no GridProduto e desce por props. Não crie `useState` de filtro aqui
// "para simplificar": a sidebar e o bottom-sheet ficam MONTADOS ao mesmo tempo
// (só o CSS esconde um deles), então um estado local seria uma segunda fonte de
// verdade e o desencontro apareceria no instante em que a tela cruzasse 768px —
// girar um tablet mostraria um conjunto de marcações no painel e outro resultado
// na grade, sem nada no código apontando para a causa.
import React, { useEffect, useRef, useState } from "react";
import { m, AnimatePresence, useReducedMotion } from "motion/react";

// Bloco genérico "título + lista", igual ao que existia inline no GridProduto.
const FilterSection = ({ titulo, children }) => (
  <div className="mb-6">
    <h4 className="text-white font-bold mb-3">{titulo}</h4>
    <div className="flex flex-col gap-2">{children}</div>
  </div>
);

// Tira acento e caixa para a barrinha achar "Educacao" digitando "educação" e
// vice-versa. Sem isto, metade das categorias da casa (Comunicação, Educação,
// Administração) só é encontrada por quem acerta o acento na primeira tentativa.
const semAcento = (s) =>
  String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// A etiqueta de contagem da faceta. Ela é o que separa uma lista de categorias
// de uma BUSCA FACETADA: sem o número, escolher "Direito" é um salto no escuro
// e a pessoa descobre que não havia nada só depois de a grade esvaziar.
const Contagem = ({ n, ativa }) => (
  <span
    className={
      "ml-auto shrink-0 rounded px-1.5 py-0.5 text-[11px] font-semibold tabular-nums " +
      (ativa ? "bg-insigne-orange/20 text-insigne-orange" : "bg-white/[0.07] text-white/45")
    }
  >
    {n}
  </span>
);

// ---------------------------------------------------------------------------
// O grupo de filtro. Hoje é UM: Categorias.
//
// PREÇO e DISPONIBILIDADE saíram em 09/09/2026 e TIPO em 10/09/2026, a pedido
// do cliente — a marcação e a regra, ver o comentário no GridProduto. O que
// sobrou não é uma lista menor: é uma lista em que a categoria deixou de
// dividir a atenção com mais três grupos e ficou com a coluna inteira.
//
// TIPO saiu por não repartir nada: quase todo título do acervo tem preço
// digital E impresso, então "Físico e Ebook" devolvia praticamente o catálogo
// inteiro e as outras duas opções devolviam quase nada. Um filtro cuja resposta
// é sempre a mesma lista ensina a pessoa a ignorar o painel.
//
// `variante` muda o bloco de Categorias: dropdown no drawer, lista no desktop.
// `categorias` chega já normalizada em nomes (strings) — quem sabe ler o
// formato cru da API é o GridProduto, não este componente.
// ---------------------------------------------------------------------------
const PainelFiltros = ({
  variante = "sidebar",
  categorias = [],
  categoriaSel,
  aoEscolherCategoria,
  contagemPorCategoria,
  totalSemCategoria = 0,
  buscaCategoria = "",
  aoBuscarCategoria,
}) => {
  const noDrawer = variante === "drawer";
  const conta = (nome) => (contagemPorCategoria ? contagemPorCategoria.get(nome) || 0 : 0);

  const termo = semAcento(buscaCategoria).trim();
  // A categoria SELECIONADA nunca é escondida pela barrinha. Sumir com o filtro
  // que está ligado deixaria a pessoa com uma grade filtrada e nenhum jeito de
  // desligar dali — o chip lá em cima resolveria, mas ela não tem por que saber
  // disso enquanto olha para o painel.
  const visiveis = categorias.filter(
    (nome) => !termo || nome === categoriaSel || semAcento(nome).includes(termo)
  );

  const barrinha = (
    <div className="relative mb-3">
      <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-xs text-white/35 pointer-events-none" />
      <input
        type="search"
        value={buscaCategoria}
        onChange={(e) => aoBuscarCategoria(e.target.value)}
        placeholder="Buscar categoria..."
        aria-label="Buscar categoria"
        className="foco-laranja w-full rounded-md border border-line bg-white/5 py-2 pl-8 pr-3
                   text-sm text-white placeholder:text-white/35 outline-none
                   transition-colors focus:border-insigne-orange/50 focus:bg-white/[0.07]"
      />
    </div>
  );

  return (
    <>
      <FilterSection titulo="Categorias">
        {/* A barrinha só aparece quando há lista para valer. Com seis
            categorias ela é mobília: ocupa uma linha para resolver um problema
            que não existe. */}
        {categorias.length > 8 && barrinha}

        {noDrawer ? (
          // No bottom-sheet as categorias são PASTILHAS, e não uma lista
          // vertical: elas cabem 2–3 por linha, enquanto vinte linhas empurram
          // "Tipo" para fora do alcance do polegar. Limitar a altura da lista
          // resolveria isso criando um scroll dentro do scroll, que no toque
          // prende o dedo na área errada.
          <div className="flex flex-wrap gap-2">
            {[{ nome: "", rotulo: "Todos", n: totalSemCategoria }].concat(
              visiveis.map((c) => ({ nome: c, rotulo: c, n: conta(c) }))
            ).map((c) => {
              const ativa = c.nome === (categoriaSel || "");
              // Categoria sem nenhum livro no recorte atual continua VISÍVEL,
              // porém desabilitada: escondê-la faria a lista dançar a cada
              // clique em "Tipo", e uma lista que muda de tamanho sozinha é
              // mais difícil de usar do que uma com itens apagados.
              const vazia = !ativa && c.nome !== "" && c.n === 0;
              return (
                <button
                  key={c.rotulo}
                  type="button"
                  disabled={vazia}
                  onClick={() => aoEscolherCategoria(c.nome)}
                  aria-pressed={ativa}
                  className={
                    "foco-laranja inline-flex items-center gap-2 rounded-controle border px-3.5 py-2 text-sm transition-colors " +
                    (ativa
                      ? "border-insigne-orange bg-insigne-orange/15 text-insigne-orange font-semibold"
                      : vazia
                      ? "border-line/50 bg-white/[0.02] text-white/25 cursor-not-allowed"
                      : "border-line bg-white/5 text-white/85 hover:bg-white/10")
                  }
                >
                  {c.rotulo}
                  <span className="text-[11px] tabular-nums opacity-70">{c.n}</span>
                </button>
              );
            })}
          </div>
        ) : (
          /* No desktop a lista fica SEMPRE ABERTA, e não mais atrás de um
             dropdown. Eram dois cliques para ver as opções e o painel flutuante
             cobria o resto do filtro; com Preço e Disponibilidade fora, o espaço
             que sobrou paga a lista inteira à vista — que é o ponto de uma
             busca facetada: ver de uma vez onde há material e onde não há. */
          <div
            className="max-h-[min(52vh,420px)] overflow-y-auto pr-1 -mr-1 flex flex-col gap-0.5"
            role="listbox"
            aria-label="Categorias"
          >
            {[{ nome: "", rotulo: "Todos", n: totalSemCategoria }].concat(
              visiveis.map((c) => ({ nome: c, rotulo: c, n: conta(c) }))
            ).map((c) => {
              const ativa = c.nome === (categoriaSel || "");
              const vazia = !ativa && c.nome !== "" && c.n === 0;
              return (
                <button
                  key={c.rotulo}
                  type="button"
                  role="option"
                  aria-selected={ativa}
                  disabled={vazia}
                  onClick={() => aoEscolherCategoria(c.nome)}
                  className={
                    "foco-laranja flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors " +
                    (ativa
                      ? "bg-insigne-orange/15 text-insigne-orange font-semibold"
                      : vazia
                      ? "text-white/25 cursor-not-allowed"
                      : "text-white/85 hover:bg-white/5")
                  }
                >
                  <span className="min-w-0 break-words">{c.rotulo}</span>
                  <Contagem n={c.n} ativa={ativa} />
                </button>
              );
            })}

            {visiveis.length === 0 && (
              <p className="px-3 py-4 text-sm text-white/40">
                Nenhuma categoria com “{buscaCategoria}”.
              </p>
            )}
          </div>
        )}
      </FilterSection>
    </>
  );
};

// ---------------------------------------------------------------------------
// DrawerFiltros — o mesmo painel como bottom-sheet, para o celular.
//
// Bottom-sheet e não gaveta lateral: o polegar alcança o rodapé, e é lá que
// ficam as duas ações que importam ("Limpar tudo" e "Ver N livros").
// Os filtros continuam sendo aplicados NA HORA, como no desktop — não existe
// rascunho para confirmar. Por isso o rodapé mostra a contagem já filtrada:
// é o retorno imediato de cada toque, mesmo com a grade escondida atrás.
// ---------------------------------------------------------------------------
export const DrawerFiltros = ({ aberto, onFechar, total, onLimparTudo, filtros }) => {
  const reduzirMovimento = useReducedMotion();

  useEffect(() => {
    if (!aberto) return;
    const aoTeclar = (e) => { if (e.key === "Escape") onFechar(); };
    document.addEventListener("keydown", aoTeclar);
    // Convivem duas convenções de trava de scroll no projeto: a classe
    // `body.menu-open` (App.css, usada pelo header/carrinho) e o
    // `document.body.style.overflow` direto (QuickView). Escolhemos a CLASSE de
    // propósito: o QuickView é aberto desta mesma página e, se os dois
    // escrevessem no MESMO `style.overflow`, o que fechasse por último
    // restauraria o valor errado e a página ficaria travada (ou destravada com o
    // painel ainda aberto). Classe e style inline são canais independentes.
    // O cleanup abaixo é obrigatório — o Menu.js adiciona a classe e nunca
    // remove, que é exatamente por que a trava dele não funciona. Aqui ele roda
    // tanto ao fechar quanto ao desmontar (sair da página com o painel aberto).
    document.body.classList.add("menu-open");
    return () => {
      document.removeEventListener("keydown", aoTeclar);
      document.body.classList.remove("menu-open");
    };
  }, [aberto, onFechar]);

  return (
    <AnimatePresence>
      {aberto && (
        // z-index: acima do header (z-[101]) e ABAIXO do QuickView (z-[10002]),
        // que sai desta mesma página. Sem essa ordem, espiar um livro abriria o
        // modal por baixo do painel de filtros.
        <m.div
          className="fixed inset-0 z-[9998] flex items-end justify-center bg-black/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduzirMovimento ? 0 : 0.2 }}
          onClick={onFechar}
        >
          {/* Sem `md:hidden` aqui de propósito: o botão que abre o painel já é
              só do celular, e esconder a folha por CSS ao cruzar 768px (girar o
              tablet com ela aberta) deixaria o `body.menu-open` ligado com nada
              visível para fechar — página travada sem culpado à vista. */}
          <m.div
            className="relative z-[9999] w-full max-w-[560px] max-h-[85vh] flex flex-col
                       rounded-t-2xl bg-insigne-navy-deep border border-line
                       shadow-[0_-16px_50px_rgba(0,0,0,0.55)]"
            initial={reduzirMovimento ? { opacity: 0 } : { y: "100%" }}
            animate={reduzirMovimento ? { opacity: 1 } : { y: 0 }}
            exit={reduzirMovimento ? { opacity: 0 } : { y: "100%" }}
            transition={{ duration: reduzirMovimento ? 0 : 0.28, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Filtros do catálogo"
          >
            <div className="shrink-0 px-5 pt-3 pb-3 border-b border-line">
              {/* Puxador: só decoração que diz "isto sobe do rodapé". Não há
                  arrastar para fechar — `drag` viria do bundle completo do
                  motion, e o LazyMotion do site carrega só `domAnimation`. */}
              <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/25" />
              <div className="flex items-center justify-between">
                <span className="text-white font-bold">Filtros</span>
                <button
                  onClick={onFechar}
                  aria-label="Fechar filtros"
                  className="foco-laranja w-9 h-9 rounded-full bg-white/5 border border-line
                             text-white text-xl flex items-center justify-center"
                >
                  <i className="bi bi-x" />
                </button>
              </div>
            </div>

            {/* `overscroll-contain`: sem isso, chegar ao fim da lista continua
                rolando a página atrás do painel. */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 pt-4">
              <PainelFiltros variante="drawer" {...filtros} />
            </div>

            {/* Rodapé fixo. O padding de baixo soma a safe area do iPhone: sem
                ela o botão principal fica atrás da barra de gestos. */}
            <div
              className="shrink-0 flex items-center gap-3 px-5 pt-3 border-t border-line bg-insigne-navy-deep"
              style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
            >
              <button
                onClick={onLimparTudo}
                className="foco-laranja rounded-controle border-2 border-line px-5 py-2.5 text-sm
                           font-semibold text-white/85 hover:bg-white/5 transition-colors"
              >
                Limpar tudo
              </button>
              <button
                onClick={onFechar}
                className="btn-solido foco-laranja flex-1 rounded-controle px-5 py-2.5 text-sm font-semibold"
              >
                Ver {total} livro{total === 1 ? "" : "s"}
              </button>
            </div>
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
};

// ---------------------------------------------------------------------------
// BotaoCategorias - as categorias como POPUP, e nao como coluna (11/09/2026).
//
// Em /livros e /autores elas moravam numa coluna fixa de 260px a esquerda da
// grade: espaco permanente para um controle que se usa UMA VEZ e fecha.
// Escolhida a categoria, a coluna continuava la, ocupando quase um quinto da
// largura util, ate a pessoa sair da pagina.
//
// O dono viu o popup no acervo do Clube e pediu o mesmo aqui - "e ate da a
// possibilidade de expandir os resultados para a esquerda". E exatamente o que
// se ganha: a grade herda os 260px da coluna MAIS os 40px do vao ao lado dela,
// e cabe uma coluna inteira de cards a mais nas telas largas.
//
// ================= UM BOTAO, DOIS PAINEIS =================
//
// Do tablet para cima ele abre o popup ancorado nele mesmo. No celular abre o
// bottom-sheet que ja existia (`DrawerFiltros`), porque um popup de 300px preso
// a um botao numa tela de 360px e um bottom-sheet pior feito. Quem decide e a
// mesma consulta de largura do breakpoint `md` do Tailwind, escrita UMA vez
// aqui em vez de repetida em cada pagina.
//
// Sem `aoAbrirNoCelular` (o caso do acervo do Clube, que nao tem bottom-sheet)
// o popup vale em qualquer largura - e isso ou um botao que nao faz nada.
//
// ================= POR QUE ELE FECHA SOZINHO =================
//
//  - Esc e clique fora: preso aberto, ele tapa justamente a grade que a pessoa
//    acabou de filtrar.
//  - Ao ESCOLHER uma categoria: a escolha e unica e o resultado ja mudou atras
//    do painel. Manter aberto obrigaria a um segundo clique so para ver o que
//    o primeiro fez.
//  - Quando a janela encolhe abaixo de 768px: ali quem manda e o bottom-sheet,
//    e um popup ancorado sobraria pendurado fora de lugar.
//
// O nome da categoria escolhida aparece NO BOTAO. Era um contador redondo com
// "1" - que dizia que havia um filtro, mas nao qual, justamente agora que nao
// existe mais coluna a vista para consultar.
// ---------------------------------------------------------------------------
export const BotaoCategorias = ({ filtros, aoAbrirNoCelular, variante = "compacto", className = "" }) => {
  const [aberto, setAberto] = useState(false);
  const botao = useRef(null);
  const popup = useRef(null);
  const temSheet = typeof aoAbrirNoCelular === "function";

  const noDesktop = () =>
    typeof window.matchMedia !== "function" || window.matchMedia("(min-width: 768px)").matches;

  useEffect(() => {
    if (!aberto) return undefined;
    const aoClicar = (e) => {
      if (popup.current?.contains(e.target)) return;
      if (botao.current?.contains(e.target)) return;
      setAberto(false);
    };
    const aoTeclar = (e) => {
      if (e.key === "Escape") setAberto(false);
    };
    document.addEventListener("mousedown", aoClicar);
    document.addEventListener("keydown", aoTeclar);
    return () => {
      document.removeEventListener("mousedown", aoClicar);
      document.removeEventListener("keydown", aoTeclar);
    };
  }, [aberto]);

  useEffect(() => {
    if (!aberto || !temSheet || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia("(min-width: 768px)");
    const conferir = () => {
      if (!mq.matches) setAberto(false);
    };
    mq.addEventListener?.("change", conferir);
    return () => mq.removeEventListener?.("change", conferir);
  }, [aberto, temSheet]);

  const selecionada = filtros?.categoriaSel;

  return (
    <div className={"relative shrink-0 " + className}>
      <button
        ref={botao}
        type="button"
        onClick={() => {
          if (temSheet && !noDesktop()) aoAbrirNoCelular();
          else setAberto((v) => !v);
        }}
        aria-haspopup="dialog"
        aria-expanded={aberto}
        className={
          // As duas alturas do site, e nao uma so: em /livros e /autores o
          // botao fica ao lado de campos `rounded-md py-2`, no acervo do Clube
          // ao lado de um campo `rounded-lg py-2.5`. Um unico tamanho deixaria
          // o botao mais baixo (ou mais alto) que o vizinho em uma das telas.
          // Escolher pela prop, e nao concatenar as duas grafias: com
          // `rounded-md` e `rounded-lg` na mesma string quem decide e a ordem
          // do CSS gerado, nao a da string.
          "foco-laranja flex items-center gap-2 border text-sm transition-colors px-4 " +
          (variante === "largo" ? "rounded-lg py-2.5 " : "rounded-md py-2 ") +
          (selecionada
            ? "border-insigne-orange bg-insigne-orange/15 text-insigne-orange"
            : "border-line bg-white/5 text-white/85 hover:bg-white/10")
        }
      >
        <i className="bi bi-sliders text-xs shrink-0" />
        <span className="max-w-[150px] truncate">{selecionada || "Categorias"}</span>
        <i
          className={
            "bi bi-chevron-down text-xs shrink-0 transition-transform " + (aberto ? "rotate-180" : "")
          }
        />
      </button>

      {aberto && (
        <div
          ref={popup}
          role="dialog"
          aria-label="Categorias"
          // `right-0`: o painel cresce para a ESQUERDA a partir do botao, que
          // fica na ponta direita da faixa. Ancorado a esquerda, ele sairia da
          // tela nas larguras em que a faixa termina junto da borda.
          className="absolute right-0 z-40 mt-2 w-[min(88vw,300px)] max-h-[70vh] overflow-y-auto
                     overscroll-contain rounded-xl border border-line bg-insigne-navy-deep p-3
                     shadow-[0_16px_44px_rgba(0,0,0,0.55)]"
        >
          <PainelFiltros
            {...filtros}
            aoEscolherCategoria={(c) => {
              filtros.aoEscolherCategoria(c);
              setAberto(false);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default PainelFiltros;
